import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConstructionOrders } from '@saiens/shared/hooks';
import { formatDate, getConstructionStateLabel } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight } from 'lucide-react';

export function ConstructionListPage() {
  const [search, setSearch] = useState('');
  const [state, setState] = useState<string>('');
  const [myOrders, setMyOrders] = useState(false);

  const { data, isLoading, error } = useConstructionOrders({
    search,
    state: state || undefined,
    myOrders: myOrders || undefined,
    limit: 50,
  });

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">施工管理</h1>
      </div>

      {/* Search & Filter */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋施工單..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          <FilterChip
            label="我的"
            active={myOrders}
            onClick={() => {
              setMyOrders(!myOrders);
              if (!myOrders) {
                setState('');
              }
            }}
          />
          <FilterChip
            label="全部"
            active={!myOrders && state === ''}
            onClick={() => {
              setMyOrders(false);
              setState('');
            }}
          />
          <FilterChip
            label="安裝"
            active={state === 'install'}
            onClick={() => setState('install')}
          />
          <FilterChip
            label="進場"
            active={state === 'in_one'}
            onClick={() => setState('in_one')}
          />
          <FilterChip
            label="美容"
            active={state === 'beauty'}
            onClick={() => setState('beauty')}
          />
          <FilterChip
            label="維修"
            active={state === 'repair'}
            onClick={() => setState('repair')}
          />
          <FilterChip
            label="完工"
            active={state === 'finish'}
            onClick={() => setState('finish')}
          />
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : data?.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有符合條件的施工單</div>
        ) : (
          <div className="divide-y">
            {data?.items.map((order) => (
              <Link
                key={order.id}
                to={`/construction/${order.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{order.name}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {order.sale_order_name} • {order.partner_name || '未指定客戶'}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                    <span>{formatDate(order.date_order)}</span>
                    {order.lead_user_name && <span>• 工頭: {order.lead_user_name}</span>}
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <StateTag state={order.state} />
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Total count */}
      {data && (
        <p className="mt-4 text-center text-sm text-gray-500">共 {data.total} 筆施工單</p>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

function StateTag({ state }: { state: string }) {
  const colors: Record<string, string> = {
    install: 'bg-blue-100 text-blue-700',
    in_one: 'bg-yellow-100 text-yellow-700',
    in_two: 'bg-yellow-100 text-yellow-700',
    in_three: 'bg-yellow-100 text-yellow-700',
    beauty: 'bg-pink-100 text-pink-700',
    repair: 'bg-orange-100 text-orange-700',
    finish: 'bg-green-100 text-green-700',
  };

  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${colors[state] || 'bg-gray-100 text-gray-700'}`}>
      {getConstructionStateLabel(state)}
    </span>
  );
}
